import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button, MetaRow } from '@/ui';
import type {
  Cadence,
  Payloads,
  Resource,
  ResourceKind,
  SearchFilters,
  Source,
  Visibility,
} from './contracts';
import { initialPayload } from './contracts';
import type { Draft } from './service';
import { Card, Check, Field, Form, Row, Small, Stack } from './styles';

export function CadenceField({
  value,
  onChange,
}: {
  value: Cadence;
  onChange: (v: Cadence) => void;
}) {
  return (
    <Field>
      Notification frequency
      <select value={value} onChange={(e) => onChange(e.target.value as Cadence)}>
        <option value="none">None (in-app only)</option>
        <option value="immediate">Immediate</option>
        <option value="daily">Daily digest</option>
        <option value="weekly">Weekly digest</option>
      </select>
    </Field>
  );
}
export function SearchFields({
  value,
  onChange,
}: {
  value: SearchFilters;
  onChange: (v: SearchFilters) => void;
}) {
  return (
    <Stack>
      {(['query', 'author', 'community', 'topic', 'from', 'until'] as const).map((key) => (
        <Field key={key}>
          {
            {
              query: 'Search words',
              author: 'Author handle',
              community: 'Community slug',
              topic: 'Topic',
              from: 'From date',
              until: 'Until date',
            }[key]
          }
          <input
            type={key === 'from' || key === 'until' ? 'date' : 'text'}
            value={value[key]}
            maxLength={200}
            onChange={(e) => onChange({ ...value, [key]: e.target.value })}
          />
        </Field>
      ))}
      <Field>
        Media
        <select
          value={value.media}
          onChange={(e) => onChange({ ...value, media: e.target.value as SearchFilters['media'] })}
        >
          {['any', 'image', 'video', 'link'].map((v) => (
            <option key={v}>{v}</option>
          ))}
        </select>
      </Field>
    </Stack>
  );
}
function SourceInput({ onAdd }: { onAdd: (s: Source, note: string) => void }) {
  const [post, setPost] = useState('');
  const [comment, setComment] = useState('');
  const [note, setNote] = useState('');
  return (
    <fieldset>
      <legend>Add a source</legend>
      <Stack>
        <Field>
          Post ID
          <input value={post} onChange={(e) => setPost(e.target.value)} maxLength={80} />
        </Field>
        <Field>
          Comment ID (optional)
          <input value={comment} onChange={(e) => setComment(e.target.value)} maxLength={80} />
        </Field>
        <Field>
          Public annotation
          <textarea value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <Small>
          This text is shared with the collection. Your private bookmark notes are never copied.
        </Small>
        <Button
          type="button"
          disabled={!post.trim()}
          onClick={() => {
            const id = post.trim();
            onAdd(
              {
                postId: id,
                ...(comment.trim() ? { commentId: comment.trim() } : {}),
                title: `Post ${id}`,
                href: `/post/${encodeURIComponent(id)}${comment.trim() ? `?comment=${encodeURIComponent(comment.trim())}` : ''}`,
                available: true,
              },
              note,
            );
            setPost('');
            setComment('');
            setNote('');
          }}
        >
          Add source
        </Button>
      </Stack>
    </fieldset>
  );
}
function Ordered<T>({
  items,
  label,
  onChange,
}: {
  items: T[];
  label: (v: T) => ReactNode;
  onChange: (v: T[]) => void;
}) {
  function move(i: number, delta: number) {
    const next = [...items];
    const other = next[i + delta];
    if (other === undefined) return;
    next[i + delta] = next[i]!;
    next[i] = other;
    onChange(next);
  }
  return (
    <Stack>
      {items.map((item, i) => (
        <Card key={i}>
          <span>{label(item)}</span>
          <Row>
            <Button
              type="button"
              variant="ghost"
              aria-label={`Move item ${i + 1} up`}
              disabled={i === 0}
              onClick={() => move(i, -1)}
            >
              Up
            </Button>
            <Button
              type="button"
              variant="ghost"
              aria-label={`Move item ${i + 1} down`}
              disabled={i === items.length - 1}
              onClick={() => move(i, 1)}
            >
              Down
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onChange(items.filter((_, n) => n !== i))}
            >
              Remove item {i + 1}
            </Button>
          </Row>
        </Card>
      ))}
    </Stack>
  );
}
const localDate = (iso: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const utcDate = (s: string) => (s ? new Date(s).toISOString() : '');

/** Draft lives in the account-keyed page, never shared localStorage. Failed saves retain it. */
export function ResourceEditor({
  kind,
  resource,
  busy,
  onSave,
  onCancel,
}: {
  kind: ResourceKind;
  resource?: Resource;
  busy: boolean;
  onSave: (draft: Draft<ResourceKind>) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(resource?.title ?? '');
  const [description, setDescription] = useState(resource?.description ?? '');
  const [visibility, setVisibility] = useState<Visibility>(
    resource?.visibility ?? (kind === 'channels' ? 'public' : 'private'),
  );
  const [data, setData] = useState<Payloads[ResourceKind]>(resource?.data ?? initialPayload(kind));
  const [target, setTarget] = useState('');
  const [ruleKind, setRuleKind] = useState<'account' | 'community' | 'topic'>('topic');
  const [choiceKind, setChoiceKind] = useState<'account' | 'community' | 'mix'>('account');
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [provider, setProvider] = useState<'youtube' | 'twitch'>('youtube');
  const [blockKind, setBlockKind] = useState<'text' | 'quote'>('text');
  const [contributor, setContributor] = useState('');
  const [quotePost, setQuotePost] = useState('');
  const [quoteComment, setQuoteComment] = useState('');
  const [confirmedPublic, setConfirmedPublic] = useState(false);
  const mix = data as Payloads['mixes'];
  const pack = data as Payloads['starter-packs'];
  const collection = data as Payloads['collections'];
  const guide = data as Payloads['guides'];
  const event = data as Payloads['events'];
  const collaboration = data as Payloads['post-collaborations'];
  const channel = data as Payloads['channels'];
  const playlist = data as Payloads['media/playlists'];
  const search = data as Payloads['saved-searches'];
  const publicChange = visibility !== 'private' && resource?.visibility !== visibility;
  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy)
          onSave({ title: title.trim(), description: description.trim(), visibility, data });
      }}
    >
      <Field>
        Title
        <input required maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field>
        Description
        <textarea
          maxLength={2000}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <Field>
        Who can see this?
        <select
          value={visibility}
          disabled={kind === 'channels' || kind === 'saved-searches'}
          onChange={(e) => {
            setVisibility(e.target.value as Visibility);
            setConfirmedPublic(false);
          }}
        >
          <option value="private">Private: invited people only</option>
          <option value="unlisted">Unlisted: anyone with the link</option>
          <option value="public">Public: discoverable</option>
        </select>
      </Field>
      {publicChange && (
        <Check>
          <input
            type="checkbox"
            required
            checked={confirmedPublic}
            onChange={(e) => setConfirmedPublic(e.target.checked)}
          />
          I understand that the content and annotations will be readable by anyone with access to
          the link.
        </Check>
      )}
      {kind === 'mixes' && (
        <fieldset>
          <legend>Mix sources</legend>
          <Stack>
            <Small>
              Combine accounts, communities and topics. Existing mutes and content permissions still
              apply.
            </Small>
            <Ordered
              items={mix.rules}
              label={(r) => `${r.kind}: ${r.value}`}
              onChange={(rules) => setData({ ...mix, rules })}
            />
            <Row>
              <Field>
                Source type
                <select
                  value={ruleKind}
                  onChange={(e) => setRuleKind(e.target.value as typeof ruleKind)}
                >
                  <option value="topic">Topic</option>
                  <option value="account">Account handle</option>
                  <option value="community">Community slug</option>
                </select>
              </Field>
              <Field>
                Source
                <input value={target} maxLength={100} onChange={(e) => setTarget(e.target.value)} />
              </Field>
              <Button
                type="button"
                disabled={!target.trim() || mix.rules.length >= 30}
                onClick={() => {
                  const value = target.trim().replace(/^[@#]/, '');
                  if (!mix.rules.some((r) => r.kind === ruleKind && r.value === value))
                    setData({ ...mix, rules: [...mix.rules, { kind: ruleKind, value }] });
                  setTarget('');
                }}
              >
                Add source
              </Button>
            </Row>
            <Field>
              Default view
              <select
                value={mix.mode}
                onChange={(e) => setData({ ...mix, mode: e.target.value as typeof mix.mode })}
              >
                <option value="recent">Recent</option>
                <option value="relevant">Relevant</option>
                <option value="catch_up">Finite Catch Up</option>
              </select>
            </Field>
          </Stack>
        </fieldset>
      )}
      {kind === 'starter-packs' && (
        <fieldset>
          <legend>Pack choices</legend>
          <Stack>
            <Small>
              People choose what to follow, join or subscribe to. Nothing is selected automatically.
            </Small>
            <Ordered
              items={pack.choices}
              label={(c) => `${c.kind}: ${c.label}`}
              onChange={(choices) => setData({ choices })}
            />
            <Field>
              Choice type
              <select
                value={choiceKind}
                onChange={(e) => setChoiceKind(e.target.value as typeof choiceKind)}
              >
                <option value="account">Account</option>
                <option value="community">Community</option>
                <option value="mix">Signal Mix</option>
              </select>
            </Field>
            <Field>
              Handle or slug
              <input value={target} maxLength={100} onChange={(e) => setTarget(e.target.value)} />
            </Field>
            <Button
              type="button"
              disabled={!target.trim() || pack.choices.length >= 30}
              onClick={() => {
                const value = target.trim().replace(/^@/, '');
                if (!pack.choices.some((c) => c.kind === choiceKind && c.target === value))
                  setData({
                    choices: [
                      ...pack.choices,
                      {
                        id: crypto.randomUUID(),
                        kind: choiceKind,
                        target: value,
                        label: value,
                        state: 'available',
                      },
                    ],
                  });
                setTarget('');
              }}
            >
              Add choice
            </Button>
          </Stack>
        </fieldset>
      )}
      {kind === 'collections' && (
        <>
          <Ordered
            items={collection.items}
            label={(i) => `${i.source.title}: ${i.annotation}`}
            onChange={(items) => setData({ ...collection, items })}
          />
          <SourceInput
            onAdd={(source, annotation) =>
              setData({
                ...collection,
                items: [...collection.items, { id: crypto.randomUUID(), source, annotation }],
              })
            }
          />
        </>
      )}
      {kind === 'guides' && (
        <fieldset>
          <legend>Guide blocks</legend>
          <Stack>
            <Small>
              Quotations require the contributor's explicit consent. Publishing never copies private
              notes.
            </Small>
            <Ordered
              items={guide.blocks}
              label={(b) => (
                <MetaRow>
                  {`${b.kind}: ${b.text || b.source?.title || 'Source'}`}
                  {b.kind === 'quote' ? b.consent : ''}
                </MetaRow>
              )}
              onChange={(blocks) => setData({ ...guide, blocks })}
            />
            <Field>
              Block type
              <select
                value={blockKind}
                onChange={(e) => setBlockKind(e.target.value as typeof blockKind)}
              >
                <option value="text">Your own writing</option>
                <option value="quote">Contributor quotation</option>
              </select>
            </Field>
            <Field>
              Text
              <textarea value={text} maxLength={5000} onChange={(e) => setText(e.target.value)} />
            </Field>
            {blockKind === 'quote' && (
              <>
                <Field>
                  Contributor handle
                  <input
                    value={contributor}
                    maxLength={80}
                    onChange={(e) => setContributor(e.target.value)}
                  />
                </Field>
                <Field>
                  Quotation source post ID
                  <input
                    required
                    value={quotePost}
                    onChange={(e) => setQuotePost(e.target.value)}
                    maxLength={80}
                  />
                </Field>
                <Field>
                  Quotation source comment ID (optional)
                  <input
                    value={quoteComment}
                    onChange={(e) => setQuoteComment(e.target.value)}
                    maxLength={80}
                  />
                </Field>
              </>
            )}
            <Button
              type="button"
              disabled={
                !text.trim() ||
                (blockKind === 'quote' && (!contributor.trim() || !quotePost.trim()))
              }
              onClick={() => {
                setData({
                  ...guide,
                  blocks: [
                    ...guide.blocks,
                    {
                      id: crypto.randomUUID(),
                      kind: blockKind,
                      text,
                      consent: blockKind === 'quote' ? 'pending' : 'not_required',
                      ...(blockKind === 'quote'
                        ? {
                            source: {
                              postId: quotePost.trim(),
                              ...(quoteComment.trim() ? { commentId: quoteComment.trim() } : {}),
                              title: 'Quotation source',
                              href: `/post/${encodeURIComponent(quotePost.trim())}`,
                              available: true,
                            },
                            contributor: {
                              id: '',
                              handle: contributor.trim().replace(/^@/, ''),
                              name: contributor.trim(),
                            },
                          }
                        : {}),
                    },
                  ],
                });
                setText('');
              }}
            >
              Add block
            </Button>
            <SourceInput
              onAdd={(source, note) =>
                setData({
                  ...guide,
                  blocks: [
                    ...guide.blocks,
                    {
                      id: crypto.randomUUID(),
                      kind: 'source',
                      text: note,
                      source,
                      consent: 'not_required',
                    },
                  ],
                })
              }
            />
          </Stack>
        </fieldset>
      )}
      {kind === 'events' && (
        <fieldset>
          <legend>Event details</legend>
          <Stack>
            <Field>
              Format
              <select
                value={event.kind}
                onChange={(e) => setData({ ...event, kind: e.target.value as typeof event.kind })}
              >
                <option value="text_ama">Text AMA / question room</option>
                <option value="external_live">External live session</option>
              </select>
            </Field>
            <Field>
              Community slug (optional)
              <input
                value={event.community}
                maxLength={100}
                onChange={(e) => setData({ ...event, community: e.target.value })}
              />
            </Field>
            <Field>
              Starts (your local time)
              <input
                type="datetime-local"
                required
                value={localDate(event.startsAt)}
                onChange={(e) => setData({ ...event, startsAt: utcDate(e.target.value) })}
              />
            </Field>
            <Field>
              Ends (your local time)
              <input
                type="datetime-local"
                required
                min={localDate(event.startsAt)}
                value={localDate(event.endsAt)}
                onChange={(e) => setData({ ...event, endsAt: utcDate(e.target.value) })}
              />
            </Field>
            <Small>
              Times are stored in UTC and displayed in each reader's timezone. Your zone:{' '}
              {event.timezone}.
            </Small>
            {event.kind === 'external_live' && (
              <Field>
                YouTube or Twitch URL
                <input
                  type="url"
                  required
                  value={event.providerUrl}
                  onChange={(e) => setData({ ...event, providerUrl: e.target.value })}
                />
              </Field>
            )}
            <Field>
              Replay URL (optional)
              <input
                type="url"
                value={event.replayUrl}
                onChange={(e) => setData({ ...event, replayUrl: e.target.value })}
              />
            </Field>
            <Field>
              Recap
              <textarea
                value={event.recap}
                maxLength={10000}
                onChange={(e) => setData({ ...event, recap: e.target.value })}
              />
            </Field>
          </Stack>
        </fieldset>
      )}
      {kind === 'post-collaborations' && (
        <>
          <Field>
            Shared post draft
            <textarea
              required
              value={collaboration.body}
              maxLength={5000}
              onChange={(e) => setData({ ...collaboration, body: e.target.value })}
            />
          </Field>
          <Small>
            Save the draft before inviting up to three co-authors. Only the primary author can
            publish. Accepted co-authors can edit with revision conflict protection.
          </Small>
        </>
      )}
      {kind === 'channels' && (
        <>
          <Small>
            A public publishing channel is separate from following your account. Subscribers choose
            their own notification frequency. You can own one channel.
          </Small>
          <CadenceField
            value={channel.cadence}
            onChange={(cadence) => setData({ ...channel, cadence })}
          />
        </>
      )}
      {kind === 'media/playlists' && (
        <fieldset>
          <legend>External videos</legend>
          <Stack>
            <Small>
              Videos stay with YouTube or Twitch. TruXSocial does not host or transcode them.
            </Small>
            <Ordered
              items={playlist.items}
              label={(i) => (
                <MetaRow>
                  {i.title}
                  {i.provider}
                </MetaRow>
              )}
              onChange={(items) => setData({ items })}
            />
            <Field>
              Video title
              <input value={text} maxLength={120} onChange={(e) => setText(e.target.value)} />
            </Field>
            <Field>
              Provider
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value as typeof provider)}
              >
                <option value="youtube">YouTube</option>
                <option value="twitch">Twitch</option>
              </select>
            </Field>
            <Field>
              Video URL
              <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} />
            </Field>
            <Button
              type="button"
              disabled={!text.trim() || !url.trim()}
              onClick={() => {
                setData({
                  items: [
                    ...playlist.items,
                    {
                      id: crypto.randomUUID(),
                      title: text.trim(),
                      url: url.trim(),
                      provider,
                      chapters: [],
                      available: true,
                    },
                  ],
                });
                setText('');
                setUrl('');
              }}
            >
              Add video
            </Button>
          </Stack>
        </fieldset>
      )}
      {kind === 'saved-searches' && (
        <>
          <SearchFields
            value={search.filters}
            onChange={(filters) => setData({ ...search, filters })}
          />
          <CadenceField
            value={search.cadence}
            onChange={(cadence) => setData({ ...search, cadence })}
          />
        </>
      )}
      <Row>
        <Button type="submit" disabled={busy || !title.trim()}>
          {busy ? 'Saving…' : resource ? 'Save revision' : 'Create'}
        </Button>
        <Button type="button" variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </Row>
    </Form>
  );
}
